<template>
  <footer class="main-footer">
    <div class="footer-container">
      <div class="footer-column brand-column">
        <h2 class="footer-logo">{{ storeName || "SEVENWEAR" }}</h2>
        <p class="slogan">{{ storeTitle || "Élégance intemporelle" }}</p>

        <!-- Affichage dynamique des réseaux sociaux avec icônes -->
        <div class="social-icons">
          <template v-if="hasSocialLinks">
            <template v-if="socialLinks.instagram && socialLinks.instagram.length">
              <a v-for="(link, idx) in socialLinks.instagram" :key="'ig'+idx" v-show="link.active && link.url" :href="link.url" target="_blank">
                <img src="/icons/outline/brand-instagram.svg" alt="Instagram" />
              </a>
            </template>
            <template v-if="socialLinks.facebook && socialLinks.facebook.length">
               <a v-for="(link, idx) in socialLinks.facebook" :key="'fb'+idx" v-show="link.active && link.url" :href="link.url" target="_blank">
                <img src="/icons/outline/brand-facebook.svg" alt="Facebook" />
              </a>
            </template>
            <template v-if="socialLinks.tiktok && socialLinks.tiktok.length">
               <a v-for="(link, idx) in socialLinks.tiktok" :key="'tk'+idx" v-show="link.active && link.url" :href="link.url" target="_blank">
                <img src="/icons/outline/brand-tiktok.svg" alt="TikTok" />
              </a>
            </template>
            <template v-if="socialLinks.snapchat && socialLinks.snapchat.length">
               <a v-for="(link, idx) in socialLinks.snapchat" :key="'sc'+idx" v-show="link.active && link.url" :href="link.url" target="_blank">
                <img src="/icons/outline/brand-snapchat.svg" alt="Snapchat" />
              </a>
            </template>
            <template v-if="socialLinks.youtube && socialLinks.youtube.length">
               <a v-for="(link, idx) in socialLinks.youtube" :key="'yt'+idx" v-show="link.active && link.url" :href="link.url" target="_blank">
                <img src="/icons/outline/brand-youtube.svg" alt="YouTube" />
              </a>
            </template>
            <template v-if="socialLinks.whatsapp && socialLinks.whatsapp.length">
               <a v-for="(link, idx) in socialLinks.whatsapp" :key="'wa'+idx" v-show="link.active && link.url" :href="link.url" target="_blank">
                <img src="/icons/outline/brand-whatsapp.svg" alt="WhatsApp" />
              </a>
            </template>
          </template>
          <template v-else>
            <!-- Default icons if no social links are active, to match layout -->
            <a href="#"><img src="/icons/outline/brand-instagram.svg" alt="Instagram" /></a>
            <a href="#"><img src="/icons/outline/world.svg" alt="Website" /></a>
            <a href="#"><img src="/icons/outline/brand-youtube.svg" alt="YouTube" /></a>
          </template>
        </div>
      </div>

      <div class="footer-column links-column">
        <h3>MENU</h3>
        <ul>
          <li><router-link to="/">Accueil</router-link></li>
          <li><router-link to="/produits">Catalogue</router-link></li>
          <li><router-link to="/cart">Panier</router-link></li>
          <li><router-link to="/history">Historique</router-link></li>
          <li><router-link to="/blog">Blog</router-link></li>
        </ul>
      </div>

      <div class="footer-column links-column">
        <h3>SERVICE CLIENT</h3>
        <ul>
          <li><a href="#">Livraison</a></li>
          <li><a href="#">Paiement à la livraison</a></li>
          <li><a href="#">Retours et échanges</a></li>
          <li><a href="#">FAQ</a></li>
        </ul>
      </div>
    </div>

    <div class="footer-contact-row">
      <div class="contact-item" v-if="storePhone">
        <img src="/icons/outline/phone.svg" alt="Phone" class="contact-icon"/>
        <span dir="ltr">{{ storePhone }}</span>
      </div>
      <span class="contact-separator" v-if="storePhone && storeEmail">|</span>
      <div class="contact-item" v-if="storeEmail">
        <img src="/icons/outline/mail.svg" alt="Email" class="contact-icon"/>
        <span>{{ storeEmail }}</span>
      </div>
      <span class="contact-separator" v-if="(storePhone || storeEmail) && storeAddress">|</span>
      <div class="contact-item" v-if="storeAddress">
        <img src="/icons/outline/map-pin.svg" alt="Address" class="contact-icon"/>
        <a v-if="storeLocation" :href="storeLocation" target="_blank" class="address-link">{{ storeAddress }}</a>
        <span v-else>{{ storeAddress }}</span>
      </div>
    </div>

    <div class="footer-bottom">
      &copy; {{ new Date().getFullYear() }} {{ storeName || "SEVENWEAR" }}. Tous droits réservés.
    </div>
  </footer>
</template>

<script setup>
import { computed } from "vue";
import { useStoreConfig } from "../services/storeService";

const { storeConfig } = useStoreConfig();

const storeName = computed(() => storeConfig.value?.name || "SEVENWEAR");
const storeTitle = computed(() => storeConfig.value?.title || "Élégance intemporelle");
const storeEmail = computed(() => storeConfig.value?.contact_email || "contact@valoria.com");
const storePhone = computed(() => storeConfig.value?.contact_phone || "0782506334");
const storeAddress = computed(() => storeConfig.value?.contact_address || null);
const storeLocation = computed(() => storeConfig.value?.contact_location || null);

const socialLinks = computed(() => {
  if (storeConfig.value && storeConfig.value.social_links) {
    if (typeof storeConfig.value.social_links === "string") {
      try {
        return JSON.parse(storeConfig.value.social_links);
      } catch (e) {
        return {};
      }
    }
    return storeConfig.value.social_links;
  }
  return {};
});

const hasSocialLinks = computed(() => {
  if (!socialLinks.value) return false;
  return Object.values(socialLinks.value).some(arr => arr && arr.length > 0 && arr.some(link => link.active && link.url));
});
</script>

<style scoped>
.main-footer {
  background: #000000;
  color: #ffffff;
  padding: 80px 40px 40px;
  font-family: inherit;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 60px;
  text-align: left;
}

html[dir="rtl"] .footer-container {
  text-align: right;
}

.footer-column {
  flex: 1;
  min-width: 200px;
}

.brand-column {
  flex: 2;
  max-width: 400px;
}

.footer-logo {
  font-size: 0.9rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 30px;
  color: #ffffff;
  font-weight: 600;
}

.slogan {
  font-size: 2rem;
  line-height: 1.3;
  margin-bottom: 40px;
  color: #ffffff;
  font-weight: 400;
  white-space: pre-line; /* Allows line breaks if added in text */
}

.social-icons {
  display: flex;
  gap: 20px;
}

.social-icons a {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.social-icons a:hover {
  opacity: 1;
}

.social-icons img {
  width: 24px;
  height: 24px;
  filter: invert(1);
}

.links-column h3 {
  font-size: 0.9rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 25px;
  color: #ffffff;
  font-weight: 600;
}

.links-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.links-column li {
  margin-bottom: 15px;
}

.links-column a {
  color: #cccccc;
  text-decoration: none;
  font-size: 0.95rem;
  transition: color 0.3s;
}

.links-column a:hover {
  color: #ffffff;
}

.footer-contact-row {
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 30px 0;
  border-top: 1px solid #333333;
  border-bottom: 1px solid #333333;
  display: flex;
  align-items: center;
  gap: 30px;
  font-size: 0.9rem;
  color: #cccccc;
}

html[dir="rtl"] .footer-contact-row {
  flex-direction: row-reverse;
  justify-content: flex-end;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.contact-icon {
  width: 18px;
  height: 18px;
  filter: invert(1);
  opacity: 0.8;
}

.contact-separator {
  color: #555555;
}

.address-link {
  color: inherit;
  text-decoration: none;
}

.address-link:hover {
  text-decoration: underline;
}

.footer-bottom {
  max-width: 1200px;
  margin: 30px auto 0;
  font-size: 0.85rem;
  color: #888888;
  text-align: left;
}

html[dir="rtl"] .footer-bottom {
  text-align: right;
}

@media (max-width: 768px) {
  .footer-container {
    flex-direction: column;
    gap: 40px;
  }
  .footer-contact-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  .contact-separator {
    display: none;
  }
}
</style>
